/**
 * Offline drafts (REWRITE_PLAN.md §3.3).
 *
 * A half-written letter survives a closed tab, a dead battery, or a tunnel.
 * IndexedDB rather than localStorage because a draft can be long, and because
 * these must not be synchronised to a server the user is not signed in to.
 */

import { clampComposeMs } from '@toiyp/shared';

const DB_NAME = 'toiyp-drafts';
const STORE = 'drafts';

// One connection for the tab. Drafts are written on every keystroke, and
// opening a fresh connection each time left hundreds of them dangling.
let connection: Promise<IDBDatabase> | null = null;

function open(): Promise<IDBDatabase> {
  connection ??= new Promise<IDBDatabase>((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'id' });
      }
    };
    req.onsuccess = () => {
      const db = req.result;
      // Another tab upgrading, or the browser closing it: reopen next time.
      db.onversionchange = () => {
        db.close();
        connection = null;
      };
      db.onclose = () => {
        connection = null;
      };
      resolve(db);
    };
    req.onerror = () => {
      connection = null;
      reject(req.error);
    };
  });
  return connection;
}

export interface Draft {
  id: string;
  relationshipId: string;
  body: string;
  updatedAt: number;
  /** When the first keystroke of this draft landed — the start of the writing. */
  startedAt?: number;
}

/**
 * How long this draft has been in the writing (F5): first keystroke to now.
 *
 * Absent when the draft predates the field, when it has no words yet, or when
 * the clock went backwards — in every case the letter is sealed without a
 * claim about how long it took, rather than with a wrong one.
 */
export function composeMsOf(draft: Draft | undefined, now = Date.now()): number | undefined {
  if (!draft?.startedAt || !draft.body.trim()) return undefined;
  return clampComposeMs(now - draft.startedAt) ?? undefined;
}

function tx<T>(mode: IDBTransactionMode, fn: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return open().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const t = db.transaction(STORE, mode);
        const req = fn(t.objectStore(STORE));
        // Settle on the transaction, not the request: a write is only saved
        // once it commits, and a commit can still fail (e.g. quota).
        t.oncomplete = () => resolve(req.result);
        t.onerror = () => reject(t.error ?? req.error);
        t.onabort = () => reject(t.error ?? new Error('draft storage transaction aborted'));
      }),
  );
}

export const drafts = {
  all: () => tx<Draft[]>('readonly', (s) => s.getAll() as IDBRequest<Draft[]>),
  get: (id: string) =>
    tx<Draft | undefined>('readonly', (s) => s.get(id) as IDBRequest<Draft | undefined>),
  put: (draft: Draft) => tx<IDBValidKey>('readwrite', (s) => s.put(draft)),
  remove: (id: string) => tx<undefined>('readwrite', (s) => s.delete(id) as IDBRequest<undefined>),

  /** Called on sign-out. Drafts are the user's private words; do not keep them. */
  clear: () => tx<undefined>('readwrite', (s) => s.clear() as IDBRequest<undefined>),
};
